import { useState, useEffect } from "react";

interface LeaderData {
  name: string;
  color: string;
}

export type Leaders = Record<string, LeaderData>;

interface UseLeadersOptions {
  /**
   * If true, fetches leaders immediately on mount.
   * If false, you must call refetch() manually.
   */
  enabled?: boolean;
}

interface UseLeadersResult {
  leaders: Leaders;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch leader data from the API
 * Replaces static imports of leaders.json
 *
 * @example
 * const { leaders, isLoading, error } = useLeaders();
 */
export function useLeaders(options: UseLeadersOptions = {}): UseLeadersResult {
  const { enabled = true } = options;
  const [leaders, setLeaders] = useState<Leaders>({});
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);

  const fetchLeaders = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Call tcgfan-backend /api/data/leaders endpoint directly (public endpoint)
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

      const params = new URLSearchParams({
        game: "one-piece",
      });

      const url = `${apiUrl}/api/data/leaders?${params.toString()}`;
      console.log("[useLeaders] Fetching from:", url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch leaders: ${response.status}`);
      }

      const leaders = await response.json();

      // Backend already returns in the correct format
      setLeaders(leaders);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      console.error("Error fetching leaders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchLeaders();
    }
  }, [enabled]);

  return {
    leaders,
    isLoading,
    error,
    refetch: fetchLeaders,
  };
}
